import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "@/components/Toast";
import { useAuth } from "@/contexts/AuthContext";
import { ArrowLeft, Smartphone, Search, Plus, X, Pencil, Trash2, History, ChevronDown, ChevronUp } from "lucide-react";
import { SiteSelect } from "@/components/SiteSelect";

const STATUS = {
  nuevo: { label: "Nuevo", color: "#2563eb" },
  usado: { label: "Usado", color: "#16a34a" },
  antiguo: { label: "Antiguo", color: "#d97706" },
  en_reparacion: { label: "En reparación", color: "#7c3aed" },
  de_baja: { label: "De baja", color: "#BF3437" },
};

const empty = {
  brand: "", model: "", imei: "", line_number: "", status: "nuevo",
  decommission_reason: "", client_id: "", site_id: "", assigned_at: "", notes: "",
};

export default function Phones() {
  const { user } = useAuth();
  const backLink = user?.role === "admin" ? "/admin" : "/supervisor";
  const [clients, setClients] = useState([]);
  const [list, setList] = useState([]);
  const [form, setForm] = useState(empty);
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [clientFilter, setClientFilter] = useState("");
  const [expandedId, setExpandedId] = useState(null);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const load = async () => {
    try {
      const params = {};
      if (q.trim()) params.q = q.trim();
      if (statusFilter) params.status = statusFilter;
      if (clientFilter) params.client_id = clientFilter;
      const { data } = await api.get("/phones", { params });
      setList(data);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { (async () => {
    try { const { data } = await api.get("/clients"); setClients(data); } catch (e) { toast.error(formatApiError(e)); }
  })(); }, []);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [q, statusFilter, clientFilter]);

  const resetForm = () => { setForm(empty); setEditingId(null); setShowForm(false); };

  const startEdit = (p) => {
    setForm({
      brand: p.brand || "", model: p.model || "", imei: p.imei || "", line_number: p.line_number || "",
      status: p.status || "nuevo", decommission_reason: p.decommission_reason || "",
      client_id: p.client_id || "", site_id: p.site_id || "", assigned_at: p.assigned_at || "", notes: p.notes || "",
    });
    setEditingId(p.id);
    setShowForm(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (form.status === "de_baja" && !form.decommission_reason.trim()) {
      return toast.error("Para dar de baja un equipo tenés que indicar el motivo");
    }
    setBusy(true);
    try {
      const payload = {
        brand: form.brand, model: form.model, imei: form.imei,
        line_number: form.line_number || null,
        status: form.status,
        decommission_reason: form.status === "de_baja" ? form.decommission_reason : null,
        notes: form.notes || null,
      };
      if (form.client_id) {
        payload.client_id = form.client_id;
        payload.site_id = form.site_id || null;
        payload.assigned_at = form.assigned_at || null;
      } else if (editingId) {
        payload.clear_assignment = true;
      }
      if (editingId) {
        await api.patch(`/phones/${editingId}`, payload);
        toast.success("Equipo actualizado");
      } else {
        await api.post("/phones", payload);
        toast.success("Equipo agregado al inventario");
      }
      resetForm();
      load();
    } catch (e2) { toast.error(formatApiError(e2)); }
    finally { setBusy(false); }
  };

  const removePhone = async (id) => {
    if (!window.confirm("¿Eliminar este equipo del inventario? No se puede deshacer.")) return;
    try {
      await api.delete(`/phones/${id}`);
      toast.success("Equipo eliminado");
      setList((l) => l.filter((p) => p.id !== id));
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div style={{ padding: "20px 32px", maxWidth: 1200, margin: "0 auto" }} data-testid="phones-page">
      <Link to={backLink} style={{ display: "inline-flex", alignItems: "center", gap: 6, color: "var(--invict-text-muted)", fontSize: 12, marginBottom: 12, textDecoration: "none", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em" }}><ArrowLeft size={14}/> Volver</Link>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 10, marginBottom: 4 }}>
        <div>
          <h1 style={{ fontSize: 26, fontWeight: 900, margin: "4px 0 4px", color: "#0A0A0A", letterSpacing: "-0.02em" }}>Celulares Corporativos</h1>
          <p style={{ fontSize: 12, color: "var(--invict-text-muted)" }}>Control centralizado de los equipos de la empresa, con historial de asignación por objetivo.</p>
        </div>
        <button type="button" onClick={() => (showForm ? resetForm() : (setForm(empty), setEditingId(null), setShowForm(true)))} className="btn-primary" style={{ padding: "10px 16px" }} data-testid="phone-add-btn">
          {showForm ? <><X size={14} style={{ marginRight: 6 }}/>Cancelar</> : <><Plus size={14} style={{ marginRight: 6 }}/>Nuevo equipo</>}
        </button>
      </div>

      {showForm && (
        <form onSubmit={submit} className="card" style={{ padding: 16, marginTop: 14, marginBottom: 18 }} data-testid="phone-form">
          <h3 style={{ fontSize: 13, fontWeight: 800, margin: "0 0 10px", textTransform: "uppercase", letterSpacing: "0.05em" }}>{editingId ? "Editar equipo" : "Nuevo equipo"}</h3>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 10 }}>
            <div className="field"><label>Marca *</label><input required value={form.brand} onChange={(e) => set("brand", e.target.value)} placeholder="ej. Samsung" data-testid="phone-brand"/></div>
            <div className="field"><label>Modelo *</label><input required value={form.model} onChange={(e) => set("model", e.target.value)} placeholder="ej. Galaxy A34" data-testid="phone-model"/></div>
            <div className="field"><label>IMEI *</label><input required value={form.imei} onChange={(e) => set("imei", e.target.value)} data-testid="phone-imei"/></div>
            <div className="field"><label>Número de línea</label><input value={form.line_number} onChange={(e) => set("line_number", e.target.value)} placeholder="ej. 261 555-1234" data-testid="phone-line-number"/></div>
          </div>

          <div className="field"><label>Estado *</label>
            <select value={form.status} onChange={(e) => set("status", e.target.value)} data-testid="phone-status">
              {Object.entries(STATUS).map(([k, m]) => <option key={k} value={k}>{m.label}</option>)}
            </select>
          </div>
          {form.status === "de_baja" && (
            <div className="field"><label>Motivo de la baja *</label>
              <input required value={form.decommission_reason} onChange={(e) => set("decommission_reason", e.target.value)} placeholder="ej. pantalla rota sin reparación posible" data-testid="phone-decommission-reason"/>
            </div>
          )}

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 10 }}>
            <div className="field"><label>Objetivo asignado</label>
              <select value={form.client_id} onChange={(e) => { set("client_id", e.target.value); set("site_id", ""); }} data-testid="phone-client">
                <option value="">Sin asignar (stock interno)</option>
                {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            {form.client_id && <SiteSelect clientId={form.client_id} value={form.site_id} onChange={(v) => set("site_id", v)} testIdPrefix="phone"/>}
            {form.client_id && (
              <div className="field"><label>Fecha de entrega</label><input type="date" value={form.assigned_at} onChange={(e) => set("assigned_at", e.target.value)} data-testid="phone-assigned-at"/></div>
            )}
          </div>

          <div className="field"><label>Notas</label><input value={form.notes} onChange={(e) => set("notes", e.target.value)} placeholder="observaciones opcionales" data-testid="phone-notes"/></div>

          <button type="submit" disabled={busy} className="btn-primary" style={{ width: "100%", padding: 12, marginTop: 6 }} data-testid="phone-submit">
            {busy ? "Guardando..." : editingId ? "Guardar cambios" : "Agregar equipo"}
          </button>
        </form>
      )}

      <div className="card" style={{ padding: 12, marginBottom: 14, display: "flex", gap: 10, flexWrap: "wrap", alignItems: "flex-end" }} data-testid="phone-filters">
        <div className="field" style={{ margin: 0, flex: "2 1 220px" }}>
          <label>Buscar</label>
          <div style={{ position: "relative" }}>
            <Search size={14} style={{ position: "absolute", left: 10, top: 11, color: "var(--invict-text-muted)" }}/>
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="IMEI, línea, modelo u objetivo..." style={{ paddingLeft: 30 }} data-testid="phone-search"/>
          </div>
        </div>
        <div className="field" style={{ margin: 0, minWidth: 160 }}>
          <label>Estado</label>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} data-testid="phone-filter-status">
            <option value="">Todos</option>
            {Object.entries(STATUS).map(([k, m]) => <option key={k} value={k}>{m.label}</option>)}
          </select>
        </div>
        <div className="field" style={{ margin: 0, minWidth: 180 }}>
          <label>Objetivo</label>
          <select value={clientFilter} onChange={(e) => setClientFilter(e.target.value)} data-testid="phone-filter-client">
            <option value="">Todos</option>
            {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>
      </div>

      <div className="card" style={{ padding: 0, overflow: "hidden" }} data-testid="phone-list">
        {list.length === 0 && <div style={{ padding: 30, textAlign: "center", color: "var(--invict-text-muted)" }}><Smartphone size={26} style={{ opacity: 0.4, margin: "0 auto 6px" }}/><div style={{ fontSize: 13 }}>Sin equipos que coincidan con la búsqueda.</div></div>}
        {list.map((p) => {
          const st = STATUS[p.status] || STATUS.nuevo;
          const expanded = expandedId === p.id;
          const hasHistory = (p.assignment_history || []).length > 0;
          return (
            <div key={p.id} style={{ borderBottom: "1px solid var(--invict-bone-3)" }} data-testid={`phone-row-${p.id}`}>
              <div style={{ padding: "12px 16px", display: "flex", alignItems: "center", gap: 12 }}>
                <div style={{ width: 38, height: 38, borderRadius: 8, background: `${st.color}1a`, color: st.color, display: "grid", placeItems: "center", flexShrink: 0 }}>
                  <Smartphone size={18}/>
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                    <span style={{ fontWeight: 800, fontSize: 13 }}>{p.brand} {p.model}</span>
                    <span style={{ fontSize: 10, fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.05em", color: "#fff", background: st.color, padding: "2px 7px", borderRadius: 10 }} data-testid={`phone-status-badge-${p.id}`}>{st.label}</span>
                  </div>
                  <div style={{ fontSize: 11, color: "var(--invict-text-muted)", fontFamily: "monospace" }}>IMEI {p.imei}{p.line_number ? ` · Línea ${p.line_number}` : ""}</div>
                  <div style={{ fontSize: 11, color: "var(--invict-text-muted)" }}>
                    {p.client_name ? <>Asignado a <strong>{p.client_name}</strong>{p.site_name ? ` · ${p.site_name}` : ""}{p.assigned_at ? ` desde ${p.assigned_at}` : ""}</> : "Sin asignar (stock interno)"}
                  </div>
                  {p.status === "de_baja" && p.decommission_reason && <div style={{ fontSize: 11, color: "#BF3437", fontStyle: "italic" }}>Motivo de baja: {p.decommission_reason}</div>}
                  {p.notes && <div style={{ fontSize: 11, color: "var(--invict-text-muted)", fontStyle: "italic" }}>{p.notes}</div>}
                </div>
                {hasHistory && (
                  <button type="button" onClick={() => setExpandedId(expanded ? null : p.id)} title="Ver historial de asignación"
                    style={{ padding: 7, border: "1px solid var(--invict-bone-3)", background: "white", color: "#0A0A0A", borderRadius: 2, cursor: "pointer", flexShrink: 0 }}
                    data-testid={`phone-history-toggle-${p.id}`}>
                    <History size={13}/> {expanded ? <ChevronUp size={11}/> : <ChevronDown size={11}/>}
                  </button>
                )}
                <button type="button" onClick={() => startEdit(p)} title="Editar equipo"
                  style={{ padding: 7, border: "1px solid var(--invict-bone-3)", background: "white", color: "#0A0A0A", borderRadius: 2, cursor: "pointer", flexShrink: 0 }}
                  data-testid={`phone-edit-${p.id}`}>
                  <Pencil size={13}/>
                </button>
                <button type="button" onClick={() => removePhone(p.id)} title="Eliminar equipo"
                  style={{ padding: 7, border: "1px solid var(--invict-bone-3)", background: "white", color: "#BF3437", borderRadius: 2, cursor: "pointer", flexShrink: 0 }}
                  data-testid={`phone-delete-${p.id}`}>
                  <Trash2 size={13}/>
                </button>
              </div>
              {expanded && (
                <div style={{ padding: "0 16px 14px 66px" }} data-testid={`phone-history-${p.id}`}>
                  <div style={{ fontSize: 11, fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.05em", color: "var(--invict-text-muted)", marginBottom: 6 }}>Historial de asignación</div>
                  {p.assignment_history.slice().reverse().map((h, i) => (
                    <div key={i} style={{ fontSize: 11, color: "var(--invict-text-muted)", padding: "4px 0", borderTop: i === 0 ? "none" : "1px dashed var(--invict-bone-3)" }} data-testid={`phone-history-item-${p.id}-${i}`}>
                      {h.client_name || "—"}{h.site_name ? ` · ${h.site_name}` : ""} — {h.assigned_at || "?"} a {h.unassigned_at ? new Date(h.unassigned_at).toLocaleDateString("es-AR") : "?"}
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
